import { useAverageRate, useRateFloor } from '~/domain/Jobs';
import type { IAverageRateEntry } from '~/domain/Jobs/types/averageRateTypes';
import { UserProfession } from '~/enums/UserProfession';
import { useGetOfficeById } from '~/hooks/useGetOfficeById';
import { useDefaultRecommendedRate } from './useDefaultRecommendedRate';
import { useEnhancedRecommendedRate } from './useEnhancedRecommendedRate';

interface IUseShiftRateRecommendationParams {
  professionType: string;
  officeId: string;
  /** Current rate value; `null`/`undefined` allows defaulting to the recommendation. */
  value?: number | null;
  /** Form setter for the rate field. */
  onChange?: (value: number) => void;
  /** Selected shift dates — used to detect a single last-minute shift (enhanced rate). */
  dates?: Date[];
  /** Shift arrival time — combined with the date for the 48h enhanced-rate window. */
  arrivalTime?: string;
  /**
   * Receives the recommendation at the moment it prefills the rate, so the request stores what
   * the office was actually given (GOTU-7584). A later recommendation change (e.g. dates edited
   * after the prefill) is not reported, because the rate keeps its prefilled value. Only the
   * shift-request Shift Details step passes this: other screens that reuse the pay field (per-day
   * Edit Shift, Edit Open Shift, onsite interviews) are not what the office was shown.
   */
  onRecommendedRateChange?: (recommendedRate: number) => void;
}

interface IUseShiftRateRecommendationResult {
  /** The office's rate range for the profession (falls back to a neutral default). */
  profRate: IAverageRateEntry;
  /** Standard recommended (rounded average) rate — drives the likely/unlikely-to-fill copy. */
  averageRate: number;
  /** Rate to default/prefill/display — enhanced when eligible, else the standard average. */
  recommendedRate: number;
  /** Lowest selectable rate after applying the per-profession floor. */
  effectiveMin: number;
  /** True once the office's real recommendation has loaded; until then `effectiveMin` is fallback-based. */
  hasRecommendation: boolean;
}

// Neutral placeholder shown only until the office's real recommendation loads (e.g. before an
// office/profession is selected). It is never a real suggested rate — once `useAverageRate`
// returns a profession match, `recommendation` replaces it. Values carried over verbatim from the
// legacy pay fields: $18 is the absolute rate floor and $100 a safe upper bound for the stepper.
const FALLBACK_RATE: IAverageRateEntry = { average: 49, min: 18, max: 100 };

/**
 * Shared rate-recommendation logic for the shift-request pay fields (the DS stepper and the
 * legacy dropdown). Resolves the office's average rate for the selected profession, applies the
 * rate floor, defaults the field to the recommendation, and exposes both the standard average
 * (for the likelihood indicator) and the possibly-enhanced recommended rate (for the default and
 * display).
 */
export const useShiftRateRecommendation = ({
  professionType,
  officeId,
  value,
  onChange,
  dates,
  arrivalTime,
  onRecommendedRateChange,
}: IUseShiftRateRecommendationParams): IUseShiftRateRecommendationResult => {
  const { data: averageRating } = useAverageRate(officeId);

  const professionTypeCode = UserProfession.getCode(professionType) as string | undefined;
  const recommendation = professionTypeCode ? averageRating?.[professionTypeCode] : undefined;
  const profRate = recommendation || FALLBACK_RATE;
  const averageRate = Math.round(profRate.average);

  // Experiment: a single shift within 48h defaults to the enhanced rate for the `test` variant;
  // the standard `averageRate` is left untouched so the likelihood indicator keeps referencing it.
  const { recommendedRate } = useEnhancedRecommendedRate({
    recommendation,
    averageRate,
    dates,
    arrivalTime,
  });

  // useGetOfficeById falls back to the signed-in user when officeId is empty; a DSO parent with
  // no office picked must not inherit the parent's state.
  const office = useGetOfficeById(officeId);
  const officeState = officeId ? office?.address?.[0]?.state : undefined;

  const { effectiveMin } = useRateFloor({
    professionCode: professionTypeCode,
    officeState,
    averageRate: profRate.average,
    apiMin: profRate.min,
  });

  // The recommendation the field is prefilled with: only a real one, never the neutral fallback.
  const prefillRate = recommendation ? recommendedRate : null;

  // Default the rate to the office's recommended rate.
  useDefaultRecommendedRate({
    value,
    officeId,
    recommendedRate: prefillRate,
    onChange,
    onRecommendedRateChange,
  });

  return {
    profRate,
    averageRate,
    recommendedRate,
    effectiveMin,
    hasRecommendation: Boolean(recommendation),
  };
};
